import { useEffect, useState } from 'react';
import { knowledge } from '../api/knowledge';
import RequirementsV2API from '../api/requirementsV2';

/**
 * @file useSchemaCounts.js
 * @description How many schemas sit under each of the five categories.
 *
 * The sidebar names the categories, and a category without its count is a word
 * where a fact could be — "People" tells you the section exists, "People 33"
 * tells you what the platform holds. The number is the service's own: the same
 * `groups` the Schemas screen builds its boxes from.
 *
 * ── Why it is allowed to return nothing ──────────────────────────────
 *
 * The listing is admin-only, so for most accounts this must never be called at
 * all — hence `enabled`. When it is called and fails, the answer is an empty
 * map and the rows render without numbers. A zero would be a lie the sidebar
 * told with a straight face: "People 0" and "we have not loaded People yet"
 * look identical and mean opposite things.
 *
 * Fetched once per mount. These counts change when a schema is added, which is
 * rare enough that polling the navigation for it would cost more than it tells
 * anyone.
 */
export const useSchemaCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    // pageSize 1: the totals and the group breakdown come with every listing,
    // and the rows do not. Asking for 25 schemas to read five numbers off the
    // envelope is a payload nobody looks at.
    knowledge.listSchemas({ pageSize: 1 })
      .then((data) => {
        if (!live) return;
        const groups = Array.isArray(data?.groups) ? data.groups : [];
        setCounts(Object.fromEntries(groups.map((g) => [g.key, g.schemas])));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};

export default useSchemaCounts;

/**
 * How many records sit in each lifecycle domain.
 *
 * Same contract as the schema counts above, against the same reasoning: the
 * number is the service's own, it is fetched once, and a failure yields an
 * empty map so the rows render without numbers rather than claiming zero.
 */
export const useDataCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    knowledge.dataDomains()
      .then((data) => {
        if (!live) return;
        const domains = Array.isArray(data?.domains) ? data.domains : [];
        setCounts(Object.fromEntries(domains.map((d) => [d.key, d.records])));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};

/**
 * How many journeys sit under each kind.
 *
 * Counted from the list the console itself reads rather than from a summary
 * endpoint: there are 61 journeys, the console loads them anyway, and a second
 * count computed a second way is a second thing that can disagree with the
 * page it labels.
 */
export const useJourneyCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    RequirementsV2API.listJourneys({ all: true })
      .then((journeys) => {
        if (!live) return;
        setCounts((journeys || []).reduce((a, j) => (
          j?.kind ? { ...a, [j.kind]: (a[j.kind] || 0) + 1 } : a
        ), {}));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};

/**
 * How many workflow templates sit at each scope.
 *
 * The listing already returns a `scope` facet with its counts — the same one
 * the page's own dropdown is built from — so this reads that rather than
 * counting a second way.
 */
export const useTemplateCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    RequirementsV2API.listJourneyTemplates({ pageSize: 1 })
      .then((data) => {
        if (!live) return;
        // `facetOf` returns a plain { journey: 4 } map, not the {value, count}
        // array the other two facets in this file use. Read as it is rather
        // than reshaped, so there is nothing between the count the menu shows
        // and the count the page's own dropdown shows.
        const facet = data?.facets?.scope;
        setCounts(facet && typeof facet === 'object' && !Array.isArray(facet)
          ? { ...facet }
          : Object.fromEntries((facet || []).map((f) => [f.value ?? f.key, f.count])));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};

/**
 * How many platform actions each control kind governs.
 *
 * Read from the configuration index's own `kind` facet — the same numbers the
 * page's "governed by" dropdown shows — so the menu and the page cannot
 * disagree about how much of the platform a plan decides.
 */
export const useConfigurationCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    RequirementsV2API.listConfigurationIndex({ pageSize: 1 })
      .then((data) => {
        if (!live) return;
        const facet = Array.isArray(data?.facets?.kind) ? data.facets.kind : [];
        setCounts(Object.fromEntries(facet.map((f) => [f.value, f.count])));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};

/**
 * How many learning runs sit in each state of the loop.
 *
 * The runs listing already seeds all four states, zeros included — so a state
 * nothing has reached yet reads as 0 rather than vanishing from the menu,
 * which for this section is the whole point: an empty "Corrected" is the
 * loop's most important fact, not an absence to hide.
 */
export const useLearningCounts = (enabled = false) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    RequirementsV2API.listLearningRuns({ pageSize: 1 })
      .then((data) => {
        if (!live) return;
        const facet = Array.isArray(data?.facets?.state) ? data.facets.state : [];
        setCounts(Object.fromEntries(facet.map((f) => [f.value, f.count])));
      })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled]);

  return counts;
};
